// One-shot orders from the page to a mounted tile: "somebody stopped this
// camera", "your slot was approved, start".
//
// This replaced a pair of `Map<cameraId, timestamp>` held on LiveGridPage and
// read by VideoTile as `stopSignal`/`startSignal` number props. That shape
// expressed "it happened" as a VALUE, and a value has a lifetime an event
// does not: the maps were only ever added to, and VideoTile's effect fired on
// every mount reading whatever the current value was — so switching project
// scope away and back remounted a tile that read a signal from an hour ago as
// a fresh order and stopped itself the instant it appeared. A camera evicted
// once became un-startable for the rest of the session; a once-approved
// camera auto-played on every remount.
//
// Same closure-store shape as streamSlots.ts, for the same three reasons: an
// order arriving mid-render must not trip the "setState during render" rule,
// delivering one must not re-render the whole wall (an eviction used to
// re-render every tile on it, via the stopSignals Map living in page state),
// and it is testable without mounting anything.
export type TileCommand = "start" | "stop";

export interface TileCommands {
  /**
   * Delivers to whichever tiles are listening for this camera RIGHT NOW, and
   * returns how many listeners received it.
   *
   * Nothing is queued for a camera nobody is currently showing, and that is
   * the correct behaviour, not a limitation: an unmounted tile has already
   * released its lease (see useHlsStream's own unmount cleanup), so there is
   * no stream for a "stop" to stop — delivering one late would only destroy a
   * stream the operator deliberately started when they came back. For
   * "start", the server-side admission already happened when a request was
   * approved; reviving it later would light a camera on a wall because of a
   * negotiation the operator has since visually moved on from. The durable
   * channel for both facts is the toast the app raises from the same notice
   * — this is deliberately not that channel.
   */
  send: (cameraId: string, command: TileCommand) => number;
  /** Subscribe to commands for one camera. Returns the unsubscribe function. */
  subscribe: (cameraId: string, listener: (command: TileCommand) => void) => () => void;
}

export function createTileCommands(): TileCommands {
  const byCamera = new Map<string, Set<(command: TileCommand) => void>>();

  return {
    send(cameraId, command) {
      // Snapshotted before delivery: a listener may unsubscribe while being
      // notified — a "stop" that unmounts its own tile (it doesn't today,
      // but nothing here should assume it can't) is the ordinary case to
      // guard against, not a corner one.
      const listeners = [...(byCamera.get(cameraId) ?? [])];
      for (const listener of listeners) listener(command);
      return listeners.length;
    },
    subscribe(cameraId, listener) {
      let listeners = byCamera.get(cameraId);
      if (!listeners) {
        listeners = new Set();
        byCamera.set(cameraId, listeners);
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        // Dropped when empty, so a shift's worth of scope switching does not
        // leave one abandoned entry per camera ever shown on this page.
        if (listeners.size === 0) byCamera.delete(cameraId);
      };
    },
  };
}
